import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ShieldCheck, Sparkles, ArrowRight, Stethoscope, Truck, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PROCESSING_STEPS = [
  'Saving your responses…',
  'Preparing your intake for provider review…',
  'Loading treatment options…',
  'Almost done…',
];

export default function Qualifying({ categorySlug = 'weight-loss' }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('processing');   // processing | qualified
  const [stepIdx, setStepIdx] = useState(0);

  // Read the quiz answers so we can personalize the pre-qualified card.
  const answers = useMemo(() => {
    try {
      const raw = localStorage.getItem(`alfa_quiz_${categorySlug}`);
      return raw ? JSON.parse(raw).answers || {} : {};
    } catch { return {}; }
  }, [categorySlug]);
  const firstName = answers?.contact?.name?.split(' ')[0] || null;
  useEffect(() => {
    // Cycle through processing messages ~700ms each, then advance to qualified.
    let cancelled = false;
    let i = 0;
    const tick = () => {
      if (cancelled) return;
      i += 1;
      if (i >= PROCESSING_STEPS.length) {
        setPhase('qualified');
        return;
      }
      setStepIdx(i);
      timer = setTimeout(tick, 700);
    };
    let timer = setTimeout(tick, 700);
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  return (
    <div className="min-h-screen relative flex flex-col">
      <div className="telegra-bg" aria-hidden="true" />
      <Header />

      <main className="flex-1 px-4 sm:px-6 pt-[96px] sm:pt-[128px] pb-16 sm:pb-24 max-w-[1080px] mx-auto w-full">
        {phase === 'processing' && (
          <div data-testid="qualifying-processing" className="min-h-[420px] flex flex-col items-center justify-center text-center">
            <div className="relative w-20 h-20 mb-8">
              <div className="absolute inset-0 rounded-full border-2 border-neutral-200" />
              <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-neutral-900 animate-spin" />
              <Sparkles className="absolute inset-0 m-auto text-neutral-900" size={26} />
            </div>
            <p className="uppercase text-[12px] tracking-[0.24em] text-neutral-950">Preparing your intake</p>
            <p key={stepIdx} className="mt-4 font-display uppercase text-[28px] sm:text-[36px] md:text-[44px] leading-[1] tracking-[-0.015em] text-neutral-950 max-w-2xl animate-[fadeIn_500ms_ease]">
              {PROCESSING_STEPS[stepIdx]}
            </p>
            <div className="mt-10 w-full max-w-md">
              <div className="h-1.5 rounded-full bg-neutral-200 overflow-hidden">
                <div
                  className="h-full bg-neutral-900 transition-all duration-700 ease-out"
                  style={{ width: `${((stepIdx + 1) / PROCESSING_STEPS.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {phase === 'qualified' && (
          <div data-testid="qualifying-qualified" className="animate-[fadeIn_500ms_ease]">
            <span className="inline-flex items-center gap-2 text-emerald-700 text-[13px] uppercase tracking-[0.22em] font-semibold bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1.5">
              <CheckCircle2 size={14} /> Intake received
            </span>
            <h1 className="mt-5 font-display uppercase text-[36px] sm:text-[54px] md:text-[70px] leading-[0.98] tracking-[-0.015em] text-[color:var(--alfa-navy)]">
              {firstName ? <>Thank you, {firstName}.<br /></> : <>Thank you.<br /></>}
              <span className="text-[color:var(--alfa-royal)]">Your intake is ready for provider review.</span>
            </h1>
            <p className="mt-5 text-[17px] text-neutral-950 leading-relaxed max-w-2xl">
              A licensed provider will review your health information to determine whether treatment is medically appropriate. Submitting your information does not guarantee a prescription.
            </p>

            {/* Trust row */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              {[
                { icon: Stethoscope, t: 'Licensed U.S. Providers' },
                { icon: ShieldCheck,  t: 'HIPAA Secure' },
                { icon: CheckCircle2, t: 'Provider-Determined Treatment' },
                { icon: Truck,        t: 'Ships in 2–3 days' },
              ].map(({ icon: I, t }) => (
                <div key={t} className="flex items-center gap-2 text-[13px] text-neutral-950">
                  <I size={16} className="text-neutral-900 shrink-0" strokeWidth={1.6} />
                  <span>{t}</span>
                </div>
              ))}
            </div>

            {/* Medication picker — Semaglutide vs Tirzepatide only (P0 simplification) */}
            <div className="mt-12 rounded-2xl border border-neutral-900/10 bg-white/85 p-6 sm:p-8">
              <p className="uppercase text-[13px] tracking-[0.22em] font-bold text-neutral-950">
                Select the medication you would like your provider to consider
              </p>
              <ul className="mt-5 grid grid-cols-1 gap-2.5 text-[13.5px] text-neutral-950">
                {[
                  ['Provider review', 'of your intake before any prescription is issued'],
                  ['Medication cost', 'included in the monthly price, if prescribed'],
                  ['Shipping', 'included'],
                  ['No contracts,', 'cancel anytime'],
                ].map(([a, b]) => (
                  <li key={a} className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                    <span><strong className="text-neutral-950">{a}</strong> {b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                <MedChoiceCard
                  slug="semaglutide-injectable-b12"
                  tag="GLP-1"
                  name="Semaglutide"
                  price="$169"
                  unit="/mo*"
                  img="/images/products/semaglutide.png"
                  desc="A once-weekly compounded GLP-1 receptor agonist. A licensed provider determines whether it is medically appropriate for you."
                  chips={['Once weekly', 'GLP-1 receptor', 'Compounded']}
                  onSelect={() => navigate('/checkout/semaglutide-injectable-b12')}
                />
                <MedChoiceCard
                  slug="tirzepatide-injectable-b12"
                  tag="GLP-1 + GIP"
                  name="Tirzepatide"
                  price="$199"
                  unit="/mo*"
                  img="/images/products/tirzepatide.png"
                  desc="A once-weekly compounded GLP-1 and GIP receptor agonist. A licensed provider determines whether it is medically appropriate for you."
                  chips={['Once weekly', 'GLP-1 + GIP', 'Compounded']}
                  onSelect={() => navigate('/checkout/tirzepatide-injectable-b12')}
                />
              </div>

              <p className="mt-4 text-[12px] text-neutral-950 italic leading-relaxed">
                *Starting price. A licensed provider determines the medication and dosage, if any, that are medically appropriate. Compounded medications are not FDA-approved.
              </p>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function MedChoiceCard({ slug, tag, name, price, unit, img, desc, chips, onSelect }) {
  return (
    <article
      data-testid={`qualifying-select-${slug}`}
      className="group relative rounded-md overflow-hidden bg-white/80 backdrop-blur border border-neutral-900/10 shadow-sm hover:shadow-xl transition-all flex flex-col"
    >
      <button onClick={onSelect} className="absolute inset-0 z-10" aria-label={`Select ${name}`} />

      <div className="relative product-image aspect-[16/10] bg-[#efe4d1]">
        <img
          src={img}
          alt={name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <span className="absolute top-4 left-4 bg-white text-[11.5px] font-semibold uppercase tracking-widest px-3 py-1.5 rounded-sm text-neutral-950">
          {tag}
        </span>
      </div>

      <div className="p-5 sm:p-6 flex-1 flex flex-col">
        <div className="flex items-end justify-between gap-4">
          <h3 className="font-display uppercase text-[26px] sm:text-[34px] text-[color:var(--alfa-navy)] leading-none">
            {name}
          </h3>
          <div className="text-right shrink-0">
            <span className="font-display text-[26px] sm:text-[30px] text-neutral-950">{price}</span>
            <span className="text-[13px] text-neutral-950">{unit}</span>
          </div>
        </div>
        <p className="mt-3 text-[14.5px] text-neutral-950 leading-relaxed">{desc}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {chips.map((c) => (
            <span key={c} className="text-[12.5px] border border-neutral-300 text-neutral-950 rounded-sm px-2.5 py-1">
              {c}
            </span>
          ))}
        </div>
        <div className="mt-5 pt-4 border-t border-neutral-900/10 flex items-center justify-between">
          <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-700">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> In stock
          </p>
          <span className="arrow-suffix inline-flex uppercase font-semibold text-[13.5px] tracking-[0.14em] border-b border-neutral-900 pb-0.5 text-neutral-950 group-hover:pl-1 transition-all">
            Select this plan
          </span>
        </div>
      </div>
    </article>
  );
}
